import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { dayAndClock } from '../lib/store-check.js';
import { StoreCheckCameraEditor } from './StoreCheckCameraEditor.jsx';

// The diagnostic view: every camera each recorder has listed, its last
// picture, and what failed in the last day. Owners can rescan the recorders
// for new cameras and decide which are used. A camera that stops answering
// stays here as offline; it is never removed.

const STATUS = { found: 'Found, not used', in_use: 'In use', never_use: 'Never use' };
const STATUS_COLOR = { found: '#1565c0', in_use: '#2e7d32', never_use: '#757575' };

function CameraRow({ cam, picture, isOwner, onEdit }) {
  return (
    <div className="card" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }} data-camera={cam.id} data-status={cam.status}>
      <div style={{ width: 150, flex: '0 0 auto' }}>
        {picture
          ? <a href={picture} target="_blank" rel="noreferrer"><img src={picture} alt={`${cam.name || 'New camera'}, last picture`} loading="lazy" style={{ width: '100%', borderRadius: 6, background: '#eee', display: 'block' }} /></a>
          : <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '22px 8px', fontSize: 11, color: '#757575', textAlign: 'center' }}>{cam.last?.problem || 'No picture yet'}</div>}
      </div>
      <div style={{ flex: '1 1 180px', minWidth: 0, fontSize: 12, color: '#616161', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink, #1f2a44)' }}>{cam.name || cam.recorder_name || `Camera ${cam.channel + 1}`}</div>
        <div>
          <b style={{ color: STATUS_COLOR[cam.status] }}>{STATUS[cam.status]}</b>
          {' · '}{cam.online ? 'online' : <b style={{ color: '#b45309' }}>offline</b>}
          {' · '}recorder channel {cam.channel + 1}{cam.stream_width ? ` · ${cam.stream_width}×${cam.stream_height}` : ''}
        </div>
        {cam.sees && <div>{cam.sees}</div>}
        <div>Last picture: {cam.last_still_at ? dayAndClock(cam.last_still_at) : 'none'}</div>
        {cam.last?.problem && <div style={{ color: '#b45309' }}>Last try, {dayAndClock(cam.last.at)}: {cam.last.problem}</div>}
        {cam.last?.reading && cam.last.reading !== 'ok' && <div style={{ color: '#b45309' }}>Last reading {cam.last.reading}{cam.last.reading_error ? `: ${cam.last.reading_error}` : ''}</div>}
        <div>In the last 24 hours: {cam.failures_24h} missed picture{cam.failures_24h === 1 ? '' : 's'}, {cam.reading_errors_24h} failed reading{cam.reading_errors_24h === 1 ? '' : 's'}</div>
        {isOwner && <div><button className="btn-secondary" style={{ fontSize: 12, marginTop: 4 }} onClick={() => onEdit(cam.id)}>{cam.status === 'found' ? 'Set up' : 'Edit'}</button></div>}
      </div>
    </div>
  );
}

export function StoreCheckCameras({ isOwner }) {
  const [data, setData] = useState(null);
  const [scans, setScans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try {
      setData(await api('GET', '/api/store-check/cameras'));
      if (isOwner) setScans((await api('GET', '/api/store-check/scans/latest')).scans);
      setError('');
    } catch (e) { setError(e.message || 'Could not load the cameras'); }
  }
  useEffect(() => { load(); }, []);
  const scanning = scans.some((s) => s.open);
  useEffect(() => {
    if (!scanning || editing != null) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [scanning, editing]);

  async function rescan() {
    setAsking(true); setError('');
    try { await api('POST', '/api/store-check/rescan'); await load(); }
    catch (e) { setError(e.message || 'Could not start a rescan'); }
    finally { setAsking(false); }
  }

  // A found camera has never been in a check, so its only picture is the one
  // the rescan brought back.
  function pictureFor(cam) {
    if (cam.last?.url) return cam.last.url;
    const scan = scans.find((s) => s.location_id === cam.location_id);
    return scan?.stills.find((s) => s.channel === cam.channel)?.url || null;
  }

  const cameras = data?.cameras || [];
  const editCam = cameras.find((c) => c.id === editing);
  if (editCam) {
    return <StoreCheckCameraEditor camera={editCam} picture={pictureFor(editCam)} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />;
  }
  const found = cameras.filter((c) => c.status === 'found');
  const rest = cameras.filter((c) => c.status !== 'found');
  return (
    <div className="space-y-3">
      {error && <div className="card" style={{ color: '#b00020' }}>Error: {error}</div>}
      {isOwner && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={rescan} disabled={asking || scanning}>{scanning ? 'Rescanning…' : 'Rescan cameras'}</button>
            <span className="text-xs text-gray-500">Asks each store’s recorder which cameras it has. A new camera shows below as found and is not used until you switch it on.</span>
          </div>
          {scans.map((s) => (
            <div key={s.location_id} className="text-xs" style={{ color: s.state === 'failed' ? '#b45309' : '#616161' }} data-scan={s.store} data-scan-state={s.state}>
              {s.store}: {s.state === 'failed' ? s.message : s.state === 'done'
                ? `answered ${dayAndClock(s.asked_at)}, ${s.new_channels.length} new camera${s.new_channels.length === 1 ? '' : 's'}`
                : s.state === 'asking' ? 'asking the store (up to a minute)' : 'sending pictures'}
            </div>
          ))}
        </div>
      )}
      {found.length > 0 && <div className="text-sm font-semibold">Found, not used yet</div>}
      {found.map((c) => <CameraRow key={c.id} cam={c} picture={pictureFor(c)} isOwner={isOwner} onEdit={setEditing} />)}
      {rest.length > 0 && <div className="text-sm font-semibold">Cameras</div>}
      {rest.map((c) => <CameraRow key={c.id} cam={c} picture={pictureFor(c)} isOwner={isOwner} onEdit={setEditing} />)}
      {data && cameras.length === 0 && <div className="card text-sm text-gray-500">No cameras are known yet.</div>}
      {data?.oldest_still_at && <div className="text-xs text-gray-500">Oldest picture kept: {dayAndClock(data.oldest_still_at)}. Pictures are deleted after 30 days.</div>}
    </div>
  );
}
